/*------------------------------------------------------------------------------
   About      : Filter drawer for the NBI Insights list (epic DA-697, II-FE-2)

                Every option list is derived from the rows actually present in
                this pilot, not from the whole catalog — see `insightFilters.ts`.
                Offering an appliance the pilot does not use guarantees a
                zero-row result and makes the dropdown longer than it needs to be.

   Copyright (c) 2026 Bidgely. All rights reserved.
------------------------------------------------------------------------------*/
import { Box, Button, Drawer, Divider, MenuItem, Stack, TextField, Typography } from '@mui/material'

import { CONTENT_EDIT_STATUS } from 'types/cms/frontendTypes'

import { InsightFilters }      from '../store/insightUISlice'
import { InsightCatalog,
         InsightRecord }       from '../types/insightTypes'
import { applianceFilterOptions,
         categoryFilterOptions,
         typeFilterOptions }   from '../utils/insightFilters'

interface InsightFiltersDrawerProps {
  open        : boolean
  filters     : InsightFilters
  /** ALL rows for the pilot, unfiltered — option lists are derived from these
   *  so they do not shrink as the user narrows the view. */
  allRows     : InsightRecord[]
  catalog    ?: InsightCatalog
  onClose     : () => void
  onChange    : (patch: Partial<InsightFilters>) => void
  onReset     : () => void
}

/**
 * Values must match what `overallVerdict` actually returns.
 *
 * These previously offered `PENDING`, which the verdict never produces (it
 * returns `UNKNOWN`), so that option silently matched nothing — as did `PASS`,
 * which at the time required a semantic result nothing writes. Keep this list
 * and `ValidationVerdict` in step.
 */
const VALIDATION_OPTIONS = [
  { value : 'PASS',    label : 'Passing' },
  { value : 'WARN',    label : 'Has a warning' },
  { value : 'FAIL',    label : 'Has a failure' },
  { value : 'UNKNOWN', label : 'Nothing checked yet' },
]

export const InsightFiltersDrawer = ({
  open,
  filters,
  allRows,
  catalog,
  onClose,
  onChange,
  onReset,
}: InsightFiltersDrawerProps) => {

  const typeOptions      = typeFilterOptions(allRows, catalog)
  const applianceOptions = applianceFilterOptions(allRows, catalog)
  const categoryOptions  = categoryFilterOptions(allRows, catalog)

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box sx={{ width : 320, p : 2.5 }}>
        <Typography variant="h6" sx={{ mb : 2 }}>Filter insights</Typography>

        <Stack spacing={2}>
          <TextField
            select fullWidth size="small" label="Status"
            value={filters.editStatus}
            onChange={(e) => onChange({ editStatus : e.target.value })}
          >
            <MenuItem value="">All statuses</MenuItem>
            <MenuItem value={CONTENT_EDIT_STATUS.DRAFT}>Draft</MenuItem>
            <MenuItem value={CONTENT_EDIT_STATUS.MODIFIED}>Modified</MenuItem>
            <MenuItem value={CONTENT_EDIT_STATUS.PUBLISHED}>Published</MenuItem>
          </TextField>

          <TextField
            select fullWidth size="small" label="Type"
            value={filters.type}
            onChange={(e) => onChange({ type : e.target.value })}
          >
            <MenuItem value="">All types</MenuItem>
            {typeOptions.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
            ))}
          </TextField>

          <TextField
            select fullWidth size="small" label="Category"
            value={filters.category}
            onChange={(e) => onChange({ category : e.target.value })}
          >
            <MenuItem value="">All categories</MenuItem>
            {categoryOptions.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
            ))}
          </TextField>

          <TextField
            select fullWidth size="small" label="Appliance"
            value={filters.applianceId}
            onChange={(e) => onChange({ applianceId : e.target.value })}
          >
            <MenuItem value="">All appliances</MenuItem>
            {applianceOptions.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
            ))}
          </TextField>

          <TextField
            select fullWidth size="small" label="Validation"
            value={filters.validation}
            onChange={(e) => onChange({ validation : e.target.value })}
          >
            <MenuItem value="">All results</MenuItem>
            {VALIDATION_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
            ))}
          </TextField>
        </Stack>

        <Divider sx={{ my : 2.5 }} />

        <Stack direction="row" spacing={1}>
          <Button fullWidth variant="outlined" onClick={onReset}>Reset</Button>
          <Button fullWidth variant="contained" onClick={onClose}>Done</Button>
        </Stack>
      </Box>
    </Drawer>
  )
}

export default InsightFiltersDrawer
